/* eslint-disable complexity */
import { PlayArrow, Texture } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Drawer, Grid, IconButton, Slider, styled, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ETextureOrigin, FloorDrawerProps, IProductTexture, ITexture } from '../../../@types/ContextProviderRelated';
import { EActiveDrawer, EUnitType } from '../../../@types/Enums';
import { useAttributeActions } from '../../../hooks/useAttributeActions';
import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import { IFloor } from '../../../models/standard/FloorModel';
import { IObject } from '../../../models/standard/ObjectModel'; // Import IObject
import { ISpace } from '../../../models/standard/SpaceModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useSelection } from '../../../provider/SelectionProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { getDefaultObjectId, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { deleteButton, immersightBlue, immersightBlue75, immersightGreyLighter3 } from '../../../utils/colors';
import { FULL_CIRCLE_DEGREES, MAX_TILE_SIZE, MAX_TILE_SIZE_IMPERIAL, MIN_TILE_JOINT_WIDTH, MIN_TILE_JOINT_WIDTH_IMPERIAL } from '../../../utils/constants';
import filesApiService from '../../../utils/filesApiService';
import ConfirmationDialog from '../../modals/ConfirmationDialog';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';
import AttributeListFixed from '../AttributeListFixed';

// Styled components for tabs (from ImsTabs)
const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== 'first' && prop !== 'last',
})<{ first?: boolean; last?: boolean }>(({ first, last }) => ({
  flex: 1,
  minWidth: 0,
  maxWidth: 'none',
  padding: '10px 16px',
  margin: 0,
  textTransform: 'uppercase',
  fontWeight: 'bold',
  letterSpacing: '1px',
  fontSize: '12px',
  backgroundColor: '#e0e0e0',
  color: '#828282',
  minHeight: 'auto',
  '&.Mui-selected': {
    backgroundColor: immersightBlue,
    color: '#ffffff',
  },
  '&:hover': {
    backgroundColor: '#bdbdbd',
  },
  ...(first && {
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px',
  }),
  ...(last && {
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
  }),
}));

const FloorDrawer = ({ open, onClose, resetSelection, id }: FloorDrawerProps): JSX.Element => {
  const {
    building,
    setRoomTitle,
    updateTileProperty,
    deleteTile,
    createTileIfNeeded,
    setSelectedModel: setSelectedObject,
    setFloorTexture,
    updateRoomAttributes,
    updateFloorAttributes,
    deleteRoom,
    defaultObjects,
  } = useBuilding();
  const { selectedSpace, setActiveDrawer } = useSelection();
  const { unitType } = useUnitContext();
  const intl = useIntl();
  const { locale } = intl; // Destructure locale
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const getLocalizedObjectName = useCallback(
    (obj: IObject): string => {
      const currentLocale = locale.split('-')[0];
      if (obj.localization) {
        return obj.localization[currentLocale] || obj.localization.en || obj.name;
      }
      return obj.name;
    },
    [locale],
  );

  // ------------------- Resolve active room / space / floor -------------------
  const rooms = useMemo(() => (Array.isArray(building?.rooms) ? building.rooms.filter(Boolean) : []), [building?.rooms]);

  const activeRoom = useMemo(() => {
    if (!rooms.length) return undefined;
    const spaceId = selectedSpace?.spaceId;
    if (spaceId) {
      const containing = rooms.find((r: any) => Array.isArray(r?.spaces) && r.spaces.some((s: ISpace) => s?.uuid === spaceId));
      if (containing) return containing;
    }
    return rooms[0];
  }, [rooms, selectedSpace?.spaceId]);

  const activeSpace: ISpace | undefined = useMemo(() => {
    const arr: ISpace[] = Array.isArray(activeRoom?.spaces) ? activeRoom!.spaces.filter(Boolean) : [];
    if (!arr.length) return undefined;
    if (selectedSpace?.spaceId) {
      return arr.find((s) => s?.uuid === selectedSpace.spaceId) ?? arr[0];
    }
    return arr[0];
  }, [activeRoom?.spaces, selectedSpace?.spaceId]);

  const floor: IFloor | undefined = useMemo(() => activeSpace?.floor ?? undefined, [activeSpace?.floor]);

  // Room (title + attributes) come from the activeRoom
  const roomTitle = activeRoom?.title ?? '';
  const roomAttributeList: AttributeModel[] = activeRoom?.attributes ?? [];

  // ------------------- Local UI state -------------------
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [roomType, setRoomType] = useState<string>(roomTitle);

  const [roomDeleteDialogOpen, setRoomDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setRoomType(roomTitle);
  }, [roomTitle]);

  const hasFloorTexture = Boolean(floor?.texture && ((typeof floor.texture.color === 'string' && floor.texture.color.trim() !== '') || floor.texture.material !== null));

  // Room attributes state
  const [roomAttributes, setRoomAttributes] = useState<AttributeModel[]>(roomAttributeList ?? []);
  const [roomAttributeValues, setRoomAttributeValues] = useState<Record<number, string>>(
    (roomAttributeList ?? []).reduce(
      (acc, a) => {
        if (a.value) acc[a.id] = `${a.value}`;
        return acc;
      },
      {} as Record<number, string>,
    ),
  );
  const [roomShowInputFields, setRoomShowInputFields] = useState<Record<number, boolean>>(
    (roomAttributeList ?? []).reduce(
      (acc, a) => {
        acc[a.id] = !!a.value;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );

  // Floor attributes state
  const [floorAttributes, setFloorAttributes] = useState<AttributeModel[]>(floor?.attributes ?? []);
  const [floorAttributeValues, setFloorAttributeValues] = useState<Record<number, string>>(
    (floor?.attributes ?? []).reduce(
      (acc, a) => {
        if (a.value) acc[a.id] = `${a.value}`;
        return acc;
      },
      {} as Record<number, string>,
    ),
  );
  const [floorShowInputFields, setFloorShowInputFields] = useState<Record<number, boolean>>(
    (floor?.attributes ?? []).reduce(
      (acc, a) => {
        acc[a.id] = !!a.value;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );

  // Attribute catalog for autocomplete
  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [roomAttrSelected, setRoomAttrSelected] = useState<IAttribute | null>(null);
  const [roomAttrInputValue, setRoomAttrInputValue] = useState('');
  const [floorAttrSelected, setFloorAttrSelected] = useState<IAttribute | null>(null);
  const [floorAttrInputValue, setFloorAttrInputValue] = useState('');
  const [attrError, setAttrError] = useState<string | null>(null);

  // Re-sync when the active room/space/floor changes
  useEffect(() => {
    const rAttrs = activeRoom?.attributes ?? [];
    setRoomAttributes(rAttrs);
    setRoomAttributeValues(
      rAttrs.reduce(
        (acc, a) => {
          if (a.value) acc[a.id] = `${a.value}`;
          return acc;
        },
        {} as Record<number, string>,
      ),
    );
    setRoomShowInputFields(
      rAttrs.reduce(
        (acc, a) => {
          acc[a.id] = !!a.value;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    );

    const fAttrs = floor?.attributes ?? [];
    setFloorAttributes(fAttrs);
    setFloorAttributeValues(
      fAttrs.reduce(
        (acc, a) => {
          if (a.value) acc[a.id] = `${a.value}`;
          return acc;
        },
        {} as Record<number, string>,
      ),
    );
    setFloorShowInputFields(
      fAttrs.reduce(
        (acc, a) => {
          acc[a.id] = !!a.value;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    );
  }, [activeRoom?.uuid, activeRoom?.attributes, floor?.uuid, floor?.attributes]);

  // Load full attributes catalog when opening
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const catalog = await filesApiService.getAttributes(regionCode);
        setAllAttributes(catalog);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error fetching attributes: ${(e as Error).message}`);
      }
    })();
  }, [open, regionCode]);

  // Texture transform UI
  const initialTextureRotation = Math.round((((floor?.texture?.rotation ?? 0) * 180) / Math.PI) as number);
  const [shifting, setShifting] = useState<number>(floor?.texture?.offset ?? 0);
  const [textureRotation, setTextureRotation] = useState<number>(initialTextureRotation);

  useEffect(() => {
    setTextureRotation(Math.round((((floor?.texture?.rotation ?? 0) * 180) / Math.PI) as number));
    setShifting(floor?.texture?.offset ?? 0);
  }, [floor?.texture]);

  const updateTexture = (property: keyof ITexture, value: number) => {
    if (!floor?.texture) return;
    const updatedTexture = {
      ...floor.texture,
      [property]: property === 'rotation' ? (value * Math.PI) / 180 : value,
    };
    setFloorTexture(floor.uuid, updatedTexture);
  };

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [onClose, resetSelection]);

  // Room title (on ROOM tab)
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const next = event.target.value;
    setRoomType(next);
    setRoomTitle(next);
  };

  // --- NEW: Handle Room Unit Change ---
  const handleRoomAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      const updated = roomAttributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit, // Update Unit
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );
      setRoomAttributes(updated);
      updateRoomAttributes(updated);
    },
    [roomAttributes, updateRoomAttributes],
  );

  // --- NEW: Handle Floor Unit Change ---
  const handleFloorAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      const updated = floorAttributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit, // Update Unit
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );
      setFloorAttributes(updated);

      if (floor?.uuid) {
        updateFloorAttributes(floor.uuid, updated);
      }
    },
    [floorAttributes, updateFloorAttributes, floor?.uuid],
  );

  // Tiles controls (on FLOOR tab)
  const handleTilePropertyChange = (property: string) => (event: SyntheticEvent) => {
    let value = parseFloat((event.target as HTMLInputElement).value);
    const minTileJointWidth = unitType === EUnitType.METRIC ? MIN_TILE_JOINT_WIDTH : MIN_TILE_JOINT_WIDTH_IMPERIAL;
    const maxTileSize = unitType === EUnitType.METRIC ? MAX_TILE_SIZE : MAX_TILE_SIZE_IMPERIAL;

    if (property === 'length' || property === 'width' || property === 'jointWidth') {
      if (property === 'jointWidth' && value < minTileJointWidth) value = minTileJointWidth;
      if (value > maxTileSize) value = maxTileSize;
      if (value < 0) value = 0;

      updateTileProperty(property, value);

      if (property === 'length' || property === 'width') {
        const tiles = floor?.tiles;
        const newLength = property === 'length' ? value : tiles?.length || 0;
        const newWidth = property === 'width' ? value : tiles?.width || 0;

        if (newLength > 0 && newWidth > 0) {
          createTileIfNeeded(newLength, newWidth);
        }
      }
    } else if (property === 'rotation') {
      if (value > FULL_CIRCLE_DEGREES) value = FULL_CIRCLE_DEGREES;
      if (value < 0) value = 0;
      updateTileProperty(property, value);
    } else if (property === 'color' || property === 'jointColor') {
      updateTileProperty(property, (event.target as HTMLInputElement).value);
    }
  };

  // Room attribute actions
  const {
    handleAttributeValueChange: handleRoomAttributeValueChange,
    handleDeleteAttribute: handleRoomDeleteAttribute,
    toggleInputField: toggleRoomInputField,
    handleAttributeSelection: handleRoomAttributeSelection,
    getSortedAttributes: getSortedRoomAttributes,
  } = useAttributeActions(
    roomAttributes,
    setRoomAttributes,
    setRoomAttributeValues,
    setRoomShowInputFields,
    setRoomAttrSelected,
    setRoomAttrInputValue,
    (attrs: AttributeModel[]) => updateRoomAttributes(attrs),
    allAttributes,
    setAllAttributes,
    setAttrError,
  );

  // Floor attribute actions
  const {
    handleAttributeValueChange: handleFloorAttributeValueChange,
    handleDeleteAttribute: handleFloorDeleteAttribute,
    toggleInputField: toggleFloorInputField,
    handleAttributeSelection: handleFloorAttributeSelection,
    getSortedAttributes: getSortedFloorAttributes,
  } = useAttributeActions(
    floorAttributes,
    setFloorAttributes,
    setFloorAttributeValues,
    setFloorShowInputFields,
    setFloorAttrSelected,
    setFloorAttrInputValue,
    (attrs: AttributeModel[]) => {
      if (floor?.uuid) {
        updateFloorAttributes(floor.uuid, attrs);
      }
    },
    allAttributes,
    setAllAttributes,
    setAttrError,
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => setSelectedTab(newValue);

  // Delete room handlers
  const handleDeleteRoomClick = useCallback(() => {
    if (!activeRoom) return;
    setRoomDeleteDialogOpen(true);
  }, [activeRoom]);

  const handleConfirmDeleteRoom = useCallback(() => {
    deleteRoom();
    setRoomDeleteDialogOpen(false);
    resetSelection();
    onClose();
  }, [deleteRoom, resetSelection, onClose]);

  const handleCancelDeleteRoom = useCallback(() => {
    setRoomDeleteDialogOpen(false);
  }, []);

  // --- HELPER TO PREPARE PRODUCT TEXTURE ATTRIBUTES ---
  const getProductTextureAttributes = (texture: ITexture): AttributeModel[] => {
    if (texture.textureOrigin !== ETextureOrigin.PRODUCT) return [];
    const prodTexture = texture as IProductTexture;

    // 1. Gather Object (Category) Attributes
    const objectAttrs = prodTexture.objects?.flatMap((obj) => obj.attributes || []) || [];
    const objectAttrIds = new Set(objectAttrs.map((a) => a.id));

    // 2. Filter Texture Attributes (Product Level) that are NOT in Objects
    const productAttrs = (prodTexture.attributes || []).filter((a) => !objectAttrIds.has(a.id));

    // 3. Combine
    return [...productAttrs, ...objectAttrs];
  };
  // Dynamischer Name für den Raum (Tab 1)
  const localizedRoomName = useMemo(() => {
    const objId = getDefaultObjectId(ObjectType.Room);
    const apiObj = defaultObjects?.find((o: any) => o.id === objId);

    if (apiObj && apiObj.localization) {
      const currentLocale = locale.split('-')[0];
      return apiObj.localization[currentLocale] || apiObj.localization.en || apiObj.name;
    }
    return intl.formatMessage({ id: 'RoomDrawer.room', defaultMessage: 'Raum' });
  }, [defaultObjects, locale, intl]);

  // Dynamischer Name für den Boden (Tab 2)
  const localizedFloorName = useMemo(() => {
    const objId = getDefaultObjectId(ObjectType.Floor);
    const apiObj = defaultObjects?.find((o: any) => o.id === objId);

    if (apiObj && apiObj.localization) {
      const currentLocale = locale.split('-')[0];
      return apiObj.localization[currentLocale] || apiObj.localization.en || apiObj.name;
    }
    return intl.formatMessage({ id: 'FloorDrawer.floor', defaultMessage: 'Boden' });
  }, [defaultObjects, locale, intl]);
  return (
    <>
      <Drawer
        key={id}
        anchor="right"
        open={open}
        variant={open ? 'permanent' : 'temporary'}
        sx={{
          '& .MuiPaper-root': {
            top: '64px',
            zIndex: 1000,
            width: '420px',
            height: 'calc(100% - 64px)',
          },
        }}
      >
        <Grid container direction="column" p={0}>
          <Grid
            item
            sx={{
              backgroundColor: immersightBlue,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 0.5,
            }}
          >
            <Box sx={{ marginLeft: '16px' }}>
              <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
                {selectedTab === 0 ? localizedRoomName : localizedFloorName}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseDrawer}
              sx={{
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <PlayArrow sx={{ fontSize: 30 }} />
            </IconButton>
          </Grid>
        </Grid>

        <Grid container item direction="column" spacing={2} p={3}>
          {/* Tabs Header */}
          <Grid item container justifyContent="center" alignItems="center" sx={{ marginTop: 0, padding: 0 }}>
            <Grid item xs={12}>
              <StyledTabs value={selectedTab} onChange={handleTabChange} variant="standard" sx={{ width: '100%' }} aria-label="floor-drawer-tabs">
                <StyledTab label={localizedRoomName.toUpperCase()} first />
                <StyledTab label={localizedFloorName.toUpperCase()} last />
              </StyledTabs>
            </Grid>
          </Grid>

          {/* ROOM TAB */}
          {selectedTab === 0 && (
            <>
              <Grid item>
                <TextField label={<FormattedMessage id="FloorDrawer.roomTitle" />} variant="outlined" value={roomType} onChange={handleInputChange} fullWidth />
              </Grid>

              <Grid item>
                <AttributeList
                  attributes={getSortedRoomAttributes(roomAttributes)}
                  attributeValues={roomAttributeValues}
                  showInputFields={roomShowInputFields}
                  onAttributeValueChange={handleRoomAttributeValueChange}
                  // WIRED ROOM HANDLER
                  onAttributeUnitChange={handleRoomAttributeUnitChange}
                  onDeleteAttribute={handleRoomDeleteAttribute}
                  onToggleInputField={toggleRoomInputField}
                  onReorderAttributes={(newAttrs) => {
                    setRoomAttributes(newAttrs);
                    updateRoomAttributes(newAttrs);
                  }}
                />
              </Grid>

              <Grid item>
                <Box
                  sx={{
                    mt: -5,
                    mb: 1,
                    width: '99%',
                    mx: 'auto',
                  }}
                >
                  <AttributeAutocomplete
                    attributes={allAttributes}
                    disabledAttributeIds={roomAttributes.map((attribute) => attribute.id)}
                    selected={roomAttrSelected}
                    inputValue={roomAttrInputValue}
                    error={attrError}
                    onChange={handleRoomAttributeSelection}
                    onInputChange={(val) => {
                      setRoomAttrInputValue(val);
                      if (val !== roomAttrSelected?.key) setRoomAttrSelected(null);
                    }}
                    placeholder={intl.formatMessage({
                      id: 'AttributeAutocomplete.addAttribute',
                      defaultMessage: 'Add attribute',
                    })}
                  />
                </Box>
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteRoomClick}
                  sx={{
                    backgroundColor: deleteButton,
                  }}
                  disabled={!activeRoom}
                >
                  <FormattedMessage id="RoomDrawer.deleteRoom" defaultMessage="Raum löschen" />
                </Button>
              </Grid>
            </>
          )}

          {/* FLOOR TAB */}
          {selectedTab === 1 && (
            <>
              <Grid item>
                <AttributeList
                  attributes={getSortedFloorAttributes(floorAttributes)}
                  attributeValues={floorAttributeValues}
                  showInputFields={floorShowInputFields}
                  onAttributeValueChange={handleFloorAttributeValueChange}
                  // WIRED FLOOR HANDLER
                  onAttributeUnitChange={handleFloorAttributeUnitChange}
                  onDeleteAttribute={handleFloorDeleteAttribute}
                  onToggleInputField={toggleFloorInputField}
                  onReorderAttributes={(newAttrs) => {
                    setFloorAttributes(newAttrs);
                    // FIXED: Pass floor UUID to the update function for reordering
                    if (floor?.uuid) {
                      updateFloorAttributes(floor.uuid, newAttrs);
                    }
                  }}
                />
              </Grid>

              <Grid item>
                <Box
                  sx={{
                    mt: -5,
                    mb: 1,
                    width: '99%',
                    mx: 'auto',
                  }}
                >
                  <AttributeAutocomplete
                    attributes={allAttributes}
                    disabledAttributeIds={floorAttributes.map((attribute) => attribute.id)}
                    selected={floorAttrSelected}
                    inputValue={floorAttrInputValue}
                    error={attrError}
                    onChange={handleFloorAttributeSelection}
                    onInputChange={(val) => {
                      setFloorAttrInputValue(val);
                      if (val !== floorAttrSelected?.key) setFloorAttrSelected(null);
                    }}
                    placeholder={intl.formatMessage({
                      id: 'AttributeAutocomplete.addAttribute',
                      defaultMessage: 'Add attribute',
                    })}
                  />
                </Box>
              </Grid>

              <Grid item>
                <Typography variant="subtitle2" sx={{ color: immersightBlue }}>
                  {intl.formatMessage({
                    id: 'FloorDrawer.surface',
                    defaultMessage: 'OBERFLÄCHE',
                  })}
                </Typography>
              </Grid>

              {!hasFloorTexture && (
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<Texture />}
                    onClick={() => {
                      if (floor) {
                        setSelectedObject(floor);
                        setActiveDrawer(EActiveDrawer.TEXTURES);
                      }
                    }}
                    sx={{
                      backgroundColor: immersightBlue,
                      color: '#fff',
                      textTransform: 'uppercase',
                    }}
                  >
                    {intl.formatMessage({
                      id: 'FloorDrawer.changeFloor',
                      defaultMessage: 'BODEN LEGEN',
                    })}
                  </Button>
                </Grid>
              )}

              {floor?.texture && (floor.texture.material || floor.texture.color) && (
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: floor.texture.color || 'transparent',
                      backgroundImage: floor.texture.material ? `url(${(floor.texture.material as any)?.image?.src ?? ''})` : 'none',
                      backgroundSize: 'cover',
                      border: floor.texture.color ? '1px solid #ccc' : 'none',
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      maxWidth: 120,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {(floor.texture.name ?? '').length > 20 ? `${floor.texture.name.slice(0, 20)}...` : floor.texture.name}
                  </Typography>
                  <IconButton sx={{ color: 'red' }} onClick={() => setFloorTexture(floor.uuid, { material: null })}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              )}

              {/* --- REPLACED SECTION WITH LOCALIZED CATEGORIES --- */}
              {floor?.texture && 'objects' in floor.texture && floor.texture.objects && floor.texture.objects.length > 0 && (
                <Grid item>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    style={{
                      color: '#4a4a4a',
                      letterSpacing: '0.5px',
                    }}
                  >
                    <FormattedMessage id="RoomComponentsSearchPanel.categories" defaultMessage="Categories" />
                    {': '}
                    {/* UPDATED MAP TO USE LOCALIZED NAME */}
                    {floor.texture.objects
                      .map((obj: any) =>
                        // Use the helper here!
                        getLocalizedObjectName(obj),
                      )
                      .join(', ')}
                  </Typography>
                </Grid>
              )}

              {floor?.texture?.material && (
                <>
                  <Grid item>
                    <Typography variant="subtitle2">
                      {intl.formatMessage({
                        id: 'FloorDrawer.textureShifting',
                        defaultMessage: 'Verschieben',
                      })}
                    </Typography>
                    <Slider
                      value={shifting}
                      min={-2}
                      max={2}
                      step={0.1}
                      onChange={(_, value) => {
                        const newValue = value as number;
                        setShifting(newValue);
                        updateTexture('offset', newValue);
                      }}
                      valueLabelDisplay="auto"
                      sx={{
                        color: immersightBlue,
                        '& .MuiSlider-thumb': {
                          backgroundColor: immersightGreyLighter3,
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: immersightBlue75,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2">
                      {intl.formatMessage({
                        id: 'FloorDrawer.textureRotation',
                        defaultMessage: 'Rotieren',
                      })}
                    </Typography>
                    <Slider
                      value={textureRotation}
                      min={0}
                      max={360}
                      step={1}
                      onChange={(_, value) => {
                        const newValue = value as number;
                        setTextureRotation(newValue);
                        updateTexture('rotation', newValue);
                      }}
                      valueLabelDisplay="auto"
                      sx={{
                        color: immersightBlue,
                        '& .MuiSlider-thumb': {
                          backgroundColor: immersightGreyLighter3,
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: immersightBlue75,
                        },
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* --- TEXTURE ATTRIBUTES --- */}
              {floor?.texture &&
                floor.texture.textureOrigin === ETextureOrigin.PRODUCT &&
                (() => {
                  const prodTexture = floor.texture as IProductTexture; // Cast to access 'country'
                  const textureAttrs = getProductTextureAttributes(floor.texture);

                  if (textureAttrs.length > 0) {
                    return (
                      <Grid item>
                        <AttributeListFixed attributes={textureAttrs} originRegion={prodTexture.country} />
                      </Grid>
                    );
                  }
                  return null;
                })()}

              {/* --- FLOOR TILES (DELETION) --- */}
              {floor?.tiles && (
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={deleteTile}
                    sx={{
                      backgroundColor: deleteButton,
                    }}
                  >
                    <FormattedMessage id="FloorDrawer.deleteFloorTiles" />
                  </Button>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Drawer>

      {roomDeleteDialogOpen && (
        <ConfirmationDialog
          destructive
          open
          onClose={handleCancelDeleteRoom}
          onConfirm={handleConfirmDeleteRoom}
          confirmId="RoomDrawer.deleteRoom"
          titleId="Warnung!"
          messageId="Der gesamte Raum wird dadurch gelöscht. Sind Sie sicher?"
        />
      )}
    </>
  );
};

export default FloorDrawer;
